import React from "react";
import { Row, Col, Tooltip } from "antd";

// icons
import { faCheckCircle } from "@fortawesome/free-regular-svg-icons";
import { faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

// components
import ClassList from "common/class_card/ClassList";
import RatingModal from "common/modal/rating_modal";
import Button from "common/button";

// hooks
import { useTranslation } from "react-i18next";
import useModal from "hooks/useModal";

// utils and styles
import { Lesson, Rating } from "types";
import screenSize from "utils/screenSize";
import {
  BannerWrapper,
  BannerTitle,
  BannerContent,
  BannerIcon,
  BannerTextAndButton,
  BannerInstruction,
  BannerButtonContainer,
} from "./styles";
import ClassParticipants from "./ClassParticipants";

type TProps = {
  currentClass: Lesson;
  rating?: Rating;
};

const RateThisClassBanner = ({ currentClass, rating }: TProps) => {
  const { t } = useTranslation("class_banner");
  const [ratingModalVisible, showModal, closeModal] = useModal();

  const renderIcon = () => {
    return rating ? faCheckDouble : faCheckCircle;
  };

  const BannerTitleContent = rating
    ? t("I already studied and rated this class")
    : t("I already studied this class.");

  const BannerInstructionContent = rating
    ? t("I have reviewed my teacher and learning materials from this class.")
    : t("I still need to rate my teacher and learning materials.");

  const renderDisableRatingButton = (text: any) => {
    const tooltipMessage = () => {
      if (!currentClass.teacher) {
        return t("Cannot rate a class without teacher!");
      }
      if (!currentClass.rateable) {
        return t(
          "To ensure we all still remember what happened, classes have to be rated within 14 days."
        );
      }
      return t("Cannot rate this class");
    };

    return (
      <Tooltip placement="top" title={tooltipMessage} mouseEnterDelay={0.05}>
        <Button type="primary" disabled>
          {text}
        </Button>
      </Tooltip>
    );
  };

  const renderClickableRatingButton = (text: any) => {
    return (
      <Button type="primary" onClick={() => showModal()}>
        {text}
      </Button>
    );
  };

  const renderBannerButton = () => {
    const text = rating ? t("SEE MY RATING") : t("RATE MY CLASS");
    const clickable =
      currentClass.teacher && (currentClass.rateable || currentClass.rated);

    if (clickable) return renderClickableRatingButton(text);
    return renderDisableRatingButton(text);
  };
  const renderDownloadAnotatedPDF = () => {
    return (
      <Button
        type="primary"
        href={currentClass?.annotatedLink}
        disabled={!currentClass?.annotatedLink}
        download
      >
        {t("ANNOTATED PDF")}
      </Button>
    );
  };

  return (
    <BannerWrapper>
      <Row gutter={24}>
        <Col xs={24} sm={12} md={16} lg={18}>
          <BannerContent>
            <BannerIcon>
              <FontAwesomeIcon icon={renderIcon() as IconProp} size="2x" />
            </BannerIcon>
            <BannerTextAndButton>
              <BannerTitle>{BannerTitleContent}</BannerTitle>
              <BannerButtonContainer>
                {screenSize() === "desktop" && renderBannerButton()}
                {screenSize() === "desktop" && renderDownloadAnotatedPDF()}
              </BannerButtonContainer>
            </BannerTextAndButton>
          </BannerContent>

          {screenSize() === "mobile" && (
            <div>
              <br />
              <BannerButtonContainer>
                {renderBannerButton()}
                {renderDownloadAnotatedPDF()}
              </BannerButtonContainer>
            </div>
          )}

          <BannerInstruction>{BannerInstructionContent}</BannerInstruction>

          <ClassParticipants
            participants={currentClass.participants}
            className="participants"
          />
        </Col>
        <ClassList lessons={[currentClass]} dynamicFooter />
      </Row>

      {currentClass.teacher && (
        <RatingModal
          visible={ratingModalVisible}
          onCloseModal={() => closeModal()}
          state={currentClass}
          rating={rating}
        />
      )}
    </BannerWrapper>
  );
};

export default RateThisClassBanner;
